/**
 * ThemeToggle — light/dark switcher (ADR-0007).
 *
 * Persists в localStorage 'agentceo:theme' = light|dark.
 * Setter <html data-theme="light|dark"> at runtime.
 */
import { useEffect, useState } from 'react'
import { Icon } from '@/components/icons/Icon'

type Theme = 'light' | 'dark'

const STORAGE_KEY = 'agentceo:theme'

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'light'
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === 'dark' ? 'dark' : 'light'
}

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-theme', theme)
}

export function ThemeToggle({ className = '' }: { className?: string }) {
  const [theme, setTheme] = useState<Theme>(() => getStoredTheme())

  useEffect(() => {
    applyTheme(theme)
    localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  const next: Theme = theme === 'light' ? 'dark' : 'light'
  const labels: Record<Theme, string> = {
    light: 'Светлая тема',
    dark: 'Тёмная тема',
  }
  // Иконка показывает то, на что переключит при клике (стандартный UX-паттерн)
  const iconName = theme === 'light' ? 'moon' : 'sun'

  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      aria-label={`Переключить на ${labels[next]}. Сейчас: ${labels[theme]}`}
      title={`Переключить на ${labels[next]}`}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 transition-colors ${className}`}
    >
      <Icon name={iconName} size={18} aria-hidden="true" />
    </button>
  )
}
